import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getOpsLiveBoard, postOpsLiveBoardAction } from '../../../api/twoDays/ops.api';
import { useNow } from '../model/useWorkboard';
import { buildTeam, sortTeams } from './liveBoardModel';

const POLL_MS = 20_000;

/**
 * Live board 데이터 — 고른 프로젝트의 모든 팀 보드를 20초마다 다시 읽는다.
 * act(action) 은 서버에 보내고 돌아온 보드로 바꾼 뒤 토스트를 띄운다 (성공하면 true).
 */
const useLiveBoard = () => {
  const ctx = useOutletContext();
  const { project, toast } = ctx;
  const projectId = project?.id ?? null;
  const now = useNow();
  const [raw, setRaw] = useState(null);
  const [error, setError] = useState('');
  const pending = useRef(0);

  const load = useCallback(() => {
    if (projectId == null) return Promise.resolve();
    return getOpsLiveBoard(projectId)
      .then((b) => {
        if (pending.current > 0) return;
        setRaw(b);
        setError('');
      })
      .catch(() => setError('Couldn’t load the live board. Refresh the page.'));
  }, [projectId]);

  useEffect(() => {
    setRaw(null);
    load();
    const id = window.setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  // 시안 say() 문구
  const TOAST = {
    call: 'Question posted',
    peer_call: 'Question posted',
    note: 'Note saved · ops only',
    publish: 'Announcement sent',
    drop: 'Marked as dropped',
    reassign: 'Task reassigned',
    receiver: 'Receiver changed',
    cancel: 'Task cancelled',
    due: 'Due changed · the assignee is notified',
    no_response: null,
  };
  const act = useCallback(
    async (action, { quiet = false } = {}) => {
      if (projectId == null) return false;
      pending.current += 1;
      try {
        const b = await postOpsLiveBoardAction(projectId, action);
        setRaw(b);
        const title = TOAST[action.type];
        if (title && !quiet) toast?.({ type: 'success', state: 'white', title });
        return true;
      } catch (e) {
        if (!quiet) toast?.({ type: 'error', state: 'white', title: e?.response?.data?.msg || 'Couldn’t save. Try again.' });
        return false;
      } finally {
        pending.current = Math.max(0, pending.current - 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, toast],
  );

  const teams = useMemo(() => (raw ? sortTeams(raw.teams.map((tb) => buildTeam(tb, now, raw.logs ?? []))) : null), [raw, now]);
  const logs = raw?.logs ?? [];
  return { ctx, project, raw, teams, logs, now, error, act, reload: load, loading: raw == null && !error };
};

export default useLiveBoard;
